import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ShieldCheck, MapPin, Mail, Server, Cookie, Info } from "lucide-react";

/**
 * Privacy Policy — DPDP Act 2023 aligned.
 *
 * Structure follows the disclosure obligations under the Digital Personal Data
 * Protection Act 2023 (India) plus the Information Technology Rules, 2011:
 *   1. Categories of personal & sensitive personal data collected
 *   2. Purpose of collection
 *   3. Third-party disclosures & sub-processors
 *   4. Data location & security
 *   5. Retention window & erasure rights
 *   6. Consent withdrawal mechanism
 *   7. Cookie policy
 *   8. Medical liability disclaimer
 *   9. Grievance Officer contact
 */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <Card className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            <h1 className="font-heading text-3xl font-semibold text-slate-900">Privacy Policy</h1>
          </div>
          <p className="text-xs text-slate-500">
            Effective from: Feb 2026 · Governed by the Digital Personal Data Protection Act, 2023 (India) and the
            Information Technology (Reasonable Security Practices) Rules, 2011.
          </p>

          <section className="space-y-3 text-sm text-slate-700 leading-relaxed">
            {/* 1. WHAT WE COLLECT */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-4">1. Information We Collect</h2>
            <p>UpchaarQ (&quot;we&quot;, &quot;us&quot;, or the &quot;Platform&quot;) collects the following categories of personal
              and sensitive personal data:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><span className="font-medium">Identifiers</span> — full name, age, gender, mobile number, email address.</li>
              <li><span className="font-medium">Government IDs (hospital admins only)</span> — CMHO / clinical establishment
                registration licence, KYC documents.</li>
              <li><span className="font-medium">Government scheme cards</span> — RGHS / Ayushman Bharat / PMJAY card numbers where the
                patient opts into a cashless workflow.</li>
              <li><span className="font-medium">Sensitive Personal Health Data</span> — <strong>Medical Records, Digital
                Prescriptions, and Diagnosis Details</strong> generated by empanelled doctors on the Platform, patient chief
                complaints, medicine schedules, and advised diagnostic tests.</li>
              <li><span className="font-medium">Transaction data</span> — booking timestamps, ticket numbers, payment references
                (masked), settlement history.</li>
              <li><span className="font-medium">Technical logs</span> — mobile-masked IP metadata, device fingerprints and
                authentication events for security auditing.</li>
            </ul>
            <p>Payment card / UPI credentials are handled exclusively by our PCI-DSS-certified gateway and are
              <span className="font-medium"> never stored on UpchaarQ servers</span>.</p>

            {/* 2. PURPOSE */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">2. Purpose of Collection</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Book, verify, and manage patient appointments and hospital queues.</li>
              <li>Deliver OTPs, booking confirmations, and digital prescriptions via SMS / WhatsApp.</li>
              <li>Compute daily platform settlements and hospital commissions.</li>
              <li>Comply with government cashless-scheme (RGHS / Ayushman) audit and reimbursement obligations.</li>
              <li>Detect fraud, prevent overbooking, and enforce counter-verification for gov-scheme cards.</li>
            </ul>

            {/* 3. THIRD-PARTY DISCLOSURES */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">3. Third-Party Disclosures</h2>
            <p>UpchaarQ does <span className="font-medium">not</span> sell, rent or trade personal data. We share the minimum
              data required with the following sub-processors strictly for transactional utility:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><span className="font-medium">Razorpay</span> — payment processing, wallet top-ups, refunds.</li>
              <li><span className="font-medium">MSG91 (SMS gateway)</span> — OTP delivery, appointment confirmations, no-show
                reminders.</li>
              <li><span className="font-medium">WhatsApp Cloud API</span> — digital prescription delivery, refill reminders, gov-scheme
                verification alerts.</li>
            </ul>
            <p>No data is disclosed to advertising networks, analytics resellers, or overseas marketing partners.</p>

            {/* 4. SERVER LOCATION */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">
              <Server className="w-4 h-4 inline mr-1 text-sky-600" />4. Server Location &amp; Cross-Border Transfers
            </h2>
            <p>All personal and sensitive health data collected by UpchaarQ is <span className="font-medium">hosted strictly
              on secure cloud servers located within the geographic boundaries of India</span> (Mumbai / Hyderabad
              availability zones). Backup snapshots and disaster-recovery replicas are also India-resident.
              We do <span className="font-medium">not</span> transfer sensitive personal data outside India.</p>

            {/* 5. DATA PROTECTION */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">5. Data Protection Measures</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Passwords hashed with bcrypt; access tokens rotated every 24 hours with server-side session invalidation.</li>
              <li>All API endpoints enforce per-IP rate-limiting (max 5 OTP requests / minute) and role-based access control.</li>
              <li>All rich-text fields are stripped of HTML with <code>bleach</code> before persistence — prevents XSS.</li>
              <li>Mobile numbers and email addresses are masked in server logs.</li>
              <li>Booking queues use atomic MongoDB reservations to prevent overbooking and race conditions.</li>
              <li>Cross-tenant object-level access is blocked at the API layer (BOLA-hardened).</li>
            </ul>

            {/* 6. RETENTION & ERASURE */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">6. Retention, Erasure &amp; Consent Withdrawal</h2>
            <p>You may request permanent erasure of your account and personal data at any time via
              <code className="mx-1">DELETE /api/patient/me</code> or by writing to
              <a className="text-sky-600 hover:underline mx-1" href="mailto:support@upchaarq.in">support@upchaarq.in</a>.
              We honour valid requests within <span className="font-medium">30 days</span>.</p>
            <p><span className="font-medium">Exception — statutory retention:</span> where you have used the RGHS or Ayushman
              Bharat cashless workflow, the corresponding claim records must be retained for the duration mandated by the
              respective government scheme audit rules (typically 7 years). Only the audit-necessary fields are retained; all
              other personal data is erased in the standard 30-day window.</p>
            <p>You may <span className="font-medium">withdraw your consent</span> at any time by writing to
              <a className="text-sky-600 hover:underline mx-1" href="mailto:support@upchaarq.in">support@upchaarq.in</a>. On
              consent withdrawal we will stop further processing except for statutory retention outlined above; note that
              certain Platform features (bookings, digital prescriptions) may become unavailable to you after withdrawal.</p>
            <p>Data export in machine-readable JSON is available at any time via
              <code className="mx-1">GET /api/patient/me/data-export</code>.</p>

            {/* 7. COOKIES */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">
              <Cookie className="w-4 h-4 inline mr-1 text-amber-600" />7. Cookie Policy
            </h2>
            <p>UpchaarQ uses <span className="font-medium">only essential / functional cookies</span> required for maintaining
              secure login sessions and generating queue tokens. We do <span className="font-medium">not</span> use third-party
              advertising trackers, remarketing pixels, cross-site profiling cookies, or social-media SDK trackers.</p>

            {/* 8. MEDICAL DISCLAIMER */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">
              <Info className="w-4 h-4 inline mr-1 text-rose-600" />8. Medical Disclaimer
            </h2>
            <p>UpchaarQ is a <span className="font-medium">technology queue-management and booking tool</span>. UpchaarQ does not
              itself provide medical advice, diagnosis, prescription, or treatment. UpchaarQ holds
              <span className="font-medium"> zero liability for medical negligence, diagnosis accuracy, prescription errors, or
              any doctor-patient dispute</span>. All medical decisions remain the sole responsibility of the treating
              registered medical practitioner and the empanelled hospital / clinic.</p>

            {/* 9. GRIEVANCE OFFICER */}
            <h2 className="font-heading text-lg font-semibold text-slate-900 mt-6">
              <Mail className="w-4 h-4 inline mr-1 text-emerald-600" />9. Grievance Officer
            </h2>
            <p>In accordance with Section 8(9) of the Digital Personal Data Protection Act, 2023 and Rule 5(9) of the
              Information Technology Rules, 2011, the Grievance Officer of UpchaarQ may be contacted for any privacy-related
              concern, data-erasure request, or consent-withdrawal notice:</p>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-slate-500 mt-1" />
                <div className="text-sm">
                  <p className="font-medium text-slate-900">Grievance Officer — UpchaarQ</p>
                  <p className="text-slate-600">Email: <a className="text-sky-600 hover:underline" href="mailto:support@upchaarq.in">support@upchaarq.in</a></p>
                  <p className="text-slate-600">Response time: within 30 days of receipt.</p>
                </div>
              </div>
            </div>
          </section>
        </Card>
      </div>
    </div>
  );
}
